import React from 'react';
import {
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  StarIcon,
  DocumentTextIcon,
  CalendarIcon,
  PlayIcon,
  StopIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

export default function InterviewMeetingHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-emerald-300 via-teal-100 to-white w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Interview Meeting Management</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive interview management system with real-time scoring,
            automated question tracking, and performance analytics for effective candidate evaluation.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interview Management System Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <PlayIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Live Interview</h3>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Real-time interview management with timer tracking,
                  question navigation, and live scoring capabilities.
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                  <StarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scoring System</h3>
                <p className="text-teal-700 text-sm leading-relaxed">
                  Comprehensive scoring framework with 0-10 scale ratings,
                  total score calculation, and performance visualization.
                </p>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Question Management</h3>
                <p className="text-cyan-700 text-sm leading-relaxed">
                  Structured question handling with answer recording,
                  score assignment, and comprehensive interview documentation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Results Tracking</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Automated results compilation with detailed feedback,
                  decision support, and recruitment workflow integration.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Score Circle Visualization */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Score Visualization</h3>

              <div className="flex justify-center mb-6">
                <div className="relative">
                  <svg height="120" width="120" className="transform -rotate-90">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      stroke="#e5e7eb"
                      strokeWidth="10"
                      fill="transparent"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      stroke="#059669"
                      strokeWidth="10"
                      fill="transparent"
                      strokeDasharray="314"
                      strokeDashoffset="94.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">24 / 30</div>
                      <div className="text-xs text-gray-500 mt-1">Total Score</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interview Details */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Interview Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200">
                  <h4 className="text-lg font-bold text-emerald-900 mb-3">Meeting Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-emerald-700 font-medium">Name:</span>
                      <span className="text-emerald-900">Software Developer Interview</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-700 font-medium">Date:</span>
                      <span className="text-emerald-900">Oct 15, 2025</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-700 font-medium">Time:</span>
                      <span className="text-emerald-900">2:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-700 font-medium">Notes:</span>
                      <span className="text-emerald-900">Technical assessment</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-200">
                  <h4 className="text-lg font-bold text-teal-900 mb-3">Candidate Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-teal-700 font-medium">Interviewee:</span>
                      <span className="text-teal-900">John Smith</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-teal-700 font-medium">Role:</span>
                      <span className="text-teal-900">Software Developer</span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-teal-700 font-medium text-sm">Status:</span>
                      <span className="inline-block bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded-full">
                        In Progress
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-teal-700 font-medium text-sm">Timer:</span>
                      <span className="text-teal-900 font-mono text-sm">00:45:23</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interview Controls */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Interview Controls</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Interview Session</h2>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <button className="inline-flex items-center gap-x-2 rounded-full bg-emerald-600 px-6 py-3 text-white font-semibold shadow-lg opacity-75 cursor-not-allowed">
                      <PlayIcon className="h-5 w-5" />
                      Start Meeting
                    </button>
                    <div className="text-center">
                      <div className="text-2xl font-mono text-gray-900">00:45:23</div>
                      <div className="text-sm text-gray-500">Elapsed Time</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question Management */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Question & Scoring Interface</h3>

              <div className="space-y-6">
                {/* Question Card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                    <div className="flex items-center">
                      <span className="inline-block bg-white text-blue-600 rounded-full px-3 py-1 text-sm font-bold mr-3">
                        Q1
                      </span>
                      <h3 className="text-lg font-bold text-white">Technical Question</h3>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <p className="text-gray-900 font-medium text-lg">
                        Can you explain your experience with React and how you've used it in previous projects?
                      </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Answer
                        </label>
                        <textarea
                          rows={4}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-200 outline-none opacity-75 cursor-not-allowed"
                          placeholder="Record the candidate's answer here..."
                          disabled
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Score (0-10)
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="0"
                            max="10"
                            className="w-20 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-200 outline-none opacity-75 cursor-not-allowed"
                            disabled
                          />
                          <span className="text-gray-500">/ 10</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Questions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center mb-3">
                      <span className="inline-block bg-blue-100 text-blue-700 rounded-full px-2 py-1 text-xs font-bold mr-2">
                        Q2
                      </span>
                      <span className="text-gray-900 font-medium">Problem Solving</span>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                      Describe a challenging technical problem you solved and your approach.
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Answer recorded</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">8/10</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center mb-3">
                      <span className="inline-block bg-blue-100 text-blue-700 rounded-full px-2 py-1 text-xs font-bold mr-2">
                        Q3
                      </span>
                      <span className="text-gray-900 font-medium">Team Collaboration</span>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                      How do you approach working in a team environment?
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Answer recorded</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">9/10</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-6">
                  <button className="inline-flex items-center gap-x-2 rounded-full bg-emerald-600 px-8 py-4 text-white font-semibold shadow-lg opacity-75 cursor-not-allowed">
                    <PaperAirplaneIcon className="h-5 w-5" />
                    Submit Interview Results
                  </button>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Real-Time Scoring</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Live scoring system with immediate feedback and total score
                  calculation for comprehensive candidate evaluation.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-teal-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-teal-900">Structured Questions</h4>
                </div>
                <p className="text-teal-700 text-sm leading-relaxed">
                  Organized question management with clear numbering,
                  answer recording, and systematic evaluation process.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-cyan-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-cyan-900">Timer Integration</h4>
                </div>
                <p className="text-cyan-700 text-sm leading-relaxed">
                  Built-in timer functionality to track interview duration
                  and manage time effectively during candidate assessment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Visual Score Display</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Circular progress visualization showing total score achievement
                  with clear maximum score reference for quick assessment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Meeting Integration</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Seamless integration with video conferencing and notetaking
                  tools for comprehensive interview management experience.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Results Compilation</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Automated results compilation with detailed scoring breakdown
                  and comprehensive interview documentation for decision making.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interview Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                  <h3 className="text-lg font-bold text-emerald-900 mb-3">Preparation</h3>
                  <ul className="space-y-2 text-sm text-emerald-700">
                    <li>• Review candidate CV thoroughly before interview</li>
                    <li>• Prepare relevant technical and behavioral questions</li>
                    <li>• Set up meeting environment and test equipment</li>
                    <li>• Ensure reliable internet connection for video calls</li>
                    <li>• Have backup communication methods ready</li>
                  </ul>
                </div>

                <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
                  <h3 className="text-lg font-bold text-teal-900 mb-3">During Interview</h3>
                  <ul className="space-y-2 text-sm text-teal-700">
                    <li>• Start meeting on time and introduce yourself</li>
                    <li>• Record answers accurately and completely</li>
                    <li>• Score consistently using the 0-10 scale</li>
                    <li>• Take notes on candidate's communication skills</li>
                    <li>• Allow candidate to ask questions at the end</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-cyan-50 rounded-xl p-6 border border-cyan-200">
                  <h3 className="text-lg font-bold text-cyan-900 mb-3">Scoring Guidelines</h3>
                  <ul className="space-y-2 text-sm text-cyan-700">
                    <li>• 9-10: Exceptional answer, exceeds expectations</li>
                    <li>• 7-8: Good answer, meets requirements well</li>
                    <li>• 5-6: Adequate answer, basic understanding</li>
                    <li>• 3-4: Below average, needs improvement</li>
                    <li>• 0-2: Poor answer, significant gaps in knowledge</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Post-Interview</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Submit results immediately after interview</li>
                    <li>• Provide constructive feedback when requested</li>
                    <li>• Document any additional observations</li>
                    <li>• Follow up with candidate within agreed timeframe</li>
                    <li>• Review interview performance for process improvement</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}